import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ContactModal from '../../components/feature/ContactModal';
import SEO from '../../components/feature/SEO';

export default function About() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  useEffect(() => {
    // Google Analytics
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = 'https://www.googletagmanager.com/gtag/js?id=G-8VEFRXMPD7';
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-8VEFRXMPD7');
    `;
    document.head.appendChild(script2);

    return () => {
      document.head.removeChild(script1);
      document.head.removeChild(script2);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="About QBOVerifier – Invoice Fraud Protection for QuickBooks"
        description="Learn about QBOVerifier's mission to eliminate invoice fraud. Meet the team behind the QuickBooks security extension that protects businesses from Business Email Fraud and Business Invoice Fraud."
        keywords="about QBOVerifier, invoice fraud protection, QuickBooks security team, business email fraud, QBO verification"
        canonical="/about"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: 'About QBOVerifier',
          description: 'QBOVerifier is on a mission to eliminate invoice fraud and protect businesses with innovative verification technology.',
          url: `${import.meta.env.VITE_SITE_URL || ''}/about`,
          publisher: {
            '@type': 'Organization',
            name: 'QBOVerifier',
          },
        }}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            About QBOVerifier
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We're on a mission to eliminate invoice fraud and protect businesses from financial crime through innovative verification technology.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Our Mission
              </h2>
              <p className="text-xl text-gray-600 mb-6">
                Invoice fraud costs businesses billions of dollars annually and damages countless professional relationships. 
                We believe every business deserves protection from these sophisticated attacks.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                QBOVerifier was created to provide enterprise-grade security that's accessible to businesses of all sizes. 
                Our technology makes it impossible for fraudsters to create convincing fake invoices, protecting both 
                businesses and their clients.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">1,800+</div>
                  <div className="text-gray-600">Protected Businesses</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">$16M+</div>
                  <div className="text-gray-600">Fraud Prevented</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://static.readdy.ai/image/60cfa5760427af1c3d73f4665d1a36c9/f0d9d66a7b6474bb92675845075cbf9d.png"
                alt="Our mission"
                className="rounded-xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Meet Our Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our team combines decades of experience in cybersecurity, fintech, and enterprise software development.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <img 
                src="https://static.readdy.ai/image/60cfa5760427af1c3d73f4665d1a36c9/bc64c0f7f44e43d1696d1847bb337e34.jpeg"
                alt="CEO"
                className="w-32 h-32 rounded-full mx-auto mb-6 object-cover"
              />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Harry Hollander</h3>
              <p className="text-blue-600 font-medium mb-4">CEO & Founder</p>
              <p className="text-gray-600">
                Former IBM Systems Software Engineer. COO and CTO of Fortune 500 and ASX companies with over 40 years experience in critical leading edge technologies, cryptography and Biometrics systems. 
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <img 
                src="https://static.readdy.ai/image/60cfa5760427af1c3d73f4665d1a36c9/486e93896c028be655927e873352ab68.png"
                alt="CTO"
                className="w-32 h-32 rounded-full mx-auto mb-6 object-cover"
              />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Karen Porteous</h3>
              <p className="text-blue-600 font-medium mb-4">VP Systems Integration</p>
              <p className="text-gray-600">
                Extensive experience coordinating integration of disparate systems, and managing development teams, together with comprehensive knowledge of cryptography and secure system. 
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <img 
                src="https://static.readdy.ai/image/60cfa5760427af1c3d73f4665d1a36c9/c910eb2abda266700aad0e3e9fb90831.png"
                alt="VP Engineering"
                className="w-32 h-32 rounded-full mx-auto mb-6 object-cover"
              />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Mark Cooper</h3>
              <p className="text-blue-600 font-medium mb-4">VP of Marketing</p>
              <p className="text-gray-600">
                Senior marketing executive with 30 years of experience driving growth across technology and business sectors, specializing in cybersecurity and enterprise software solutions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Our Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These core principles guide everything we do and shape how we build products that protect businesses worldwide.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-blue-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Security First</h3>
              <p className="text-gray-600">
                Every decision we make prioritizes the security and protection of our users' data and business operations.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-lightbulb-line text-green-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Innovation</h3>
              <p className="text-gray-600">
                We continuously push the boundaries of what's possible in fraud prevention and verification technology.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-user-heart-line text-purple-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Customer Focus</h3>
              <p className="text-gray-600">
                Our users' success is our success. We build products that solve real problems and deliver genuine value.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-eye-line text-orange-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Transparency</h3>
              <p className="text-gray-600">
                We believe in open communication, clear pricing, and honest relationships with our customers and partners.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Secure Your QuickBooks?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Join thousands of businesses that trust QBOVerifier to protect their invoicing from cybercrime, their income from payment fraud, and help maintain their business reputation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => window.REACT_APP_NAVIGATE('/pricing')}
              className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors shadow-lg whitespace-nowrap"
            >
              <i className="ri-rocket-line mr-2"></i>
              Start Today!
            </button>
            <button 
              onClick={() => setIsContactModalOpen(true)}
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/10 transition-colors whitespace-nowrap"
            >
              <i className="ri-team-line mr-2"></i>
              Join Our Team
            </button>
          </div>
        </div>
      </section>

      <Footer />
      
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        title="Join Our Partner Program"
        subtitle="Let's discuss partnership opportunities with QBOVerifier"
      />
    </div>
  );
}